/**
 * Brand-tailored objective suggestion fetcher for the conversation-first wizard.
 *
 * The `offline-session` worker's `POST /objectives/suggest` endpoint accepts
 * brand context (URL + optional product research) and returns 3-4
 * brand-specific objective cards, each tagged with a `templateKey` so the
 * downstream conversation-generation step knows which prompt-shaping payload to
 * inject.
 *
 * This module is the wizard's only HTTP surface for that call. It is a tiny,
 * dependency-free wrapper:
 *   - One async function with optional fetch / workerUrl / signal injection.
 *   - Throws on non-200 - caller (the wizard) catches and silently falls back
 *     to interpolated `OBJECTIVE_TEMPLATES` and reports to PostHog.
 *   - Defensive shape validation: items with unknown `templateKey`s are
 *     filtered out so a misbehaving worker can never crash the UI.
 *
 * @module lib/suggest-objectives
 */

import type { ProductResearch } from '../types';
import type { FormTemplateKey } from './form-templates';
import {
  offlineSessionWorkerUrl,
  FALLBACK_OFFLINE_SESSION_WORKER_URL,
} from './worker-urls';

export { FALLBACK_OFFLINE_SESSION_WORKER_URL };

/**
 * Subset of `ProductResearch` the worker accepts. Mirrors the wire shape
 * declared by the `/objectives/suggest` worker handler. A separate alias
 * keeps callers from accidentally serialising fields the worker ignores
 * (`crawledPageCount`, `searchSources`, `researched_at`).
 */
export interface SuggestObjectivesProductResearch {
  summary?: string;
  keyFeatures?: string[];
  targetAudience?: string;
  valueProposition?: string;
  brandVoiceTone?: string;
}

/**
 * Body sent to `/objectives/suggest`. The worker requires AT LEAST one of
 * `brandUrl` or `productResearch.summary` to be present.
 */
export interface SuggestObjectivesInput {
  /** Optional brand URL - passes provenance through to the worker. */
  brandUrl?: string;
  /**
   * Optional product research from the brand-URL step. Pulled from
   * {@link ProductResearch} to keep the type stable across the wizard's
   * existing research-step pipeline.
   */
  productResearch?: SuggestObjectivesProductResearch | ProductResearch;
}

/**
 * One brand-tailored objective row returned by `/objectives/suggest`.
 *
 * - `title` ≤30 chars: short noun-phrase like "Product Feedback" - the only
 *   string the user sees on the row at rest. Becomes the wizard's `objective`.
 * - `hint` ≤60 chars: optional brand-specific one-liner shown as faint
 *   meta-text on the row (or under the title on hover/expand). May be empty.
 * - `templateKey`: discriminator the conversation-generation step uses to pick a
 *   `formShape`. Always one of {@link FormTemplateKey} - invalid values are
 *   filtered upstream by the worker AND defensively here, so the UI can't
 *   crash on bad data.
 */
export interface SuggestedObjective {
  title: string;
  hint: string;
  templateKey: FormTemplateKey;
  /** Optional curated template identity, validated against the library by the worker. */
  templateId?: string;
}

/**
 * Required auth plus optional injection points for tests and overrides.
 * The defaults resolve the worker URL via `offlineSessionWorkerUrl` and use
 * the global `fetch` / `AbortSignal`.
 */
export interface SuggestObjectivesOptions {
  /** Supabase access token; the worker validates it as a Bearer JWT (audit S3). */
  accessToken: string;
  /** Override for the worker base URL (defaults to `VITE_OFFLINE_SESSION_WORKER_URL`). */
  workerUrl?: string;
  /** Override for `fetch`. Defaults to the global. */
  fetchImpl?: typeof fetch;
  /** Aborts the underlying fetch. Wizard wires this up on unmount/step change. */
  signal?: AbortSignal;
}

/** Valid `FormTemplateKey` values the wizard exposes. */
const VALID_TEMPLATE_KEYS: ReadonlySet<FormTemplateKey> = new Set([
  'feedback',
  'product_walkthrough',
  'customer_onboarding',
  'custom',
]);

/**
 * Resolve the offline-session worker base URL. Thin alias for
 * `offlineSessionWorkerUrl` from `lib/worker-urls.ts`, kept so existing
 * callers (e.g. `lib/brand-research.ts`) and any external imports keep
 * working after the resolver was centralised.
 *
 * @returns Base URL with no trailing slash.
 */
export function resolveWorkerUrl(): string {
  return offlineSessionWorkerUrl();
}

/**
 * Type guard: confirm a candidate object is a usable `SuggestedObjective`.
 * Used to filter out junk items so the UI never crashes on a malformed
 * worker response.
 */
function isValidSuggestedObjective(value: unknown): value is SuggestedObjective {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  // `hint` is optional on the wire - coerce missing to empty string in the
  // filter step so the rest of the UI can rely on `string` type.
  return (
    typeof candidate.title === 'string' &&
    candidate.title.length > 0 &&
    typeof candidate.templateKey === 'string' &&
    VALID_TEMPLATE_KEYS.has(candidate.templateKey as FormTemplateKey)
  );
}

/**
 * Fetch brand-tailored objective cards from the offline-session worker.
 *
 * @param input    Brand context - at least one of `brandUrl` /
 *                 `productResearch.summary` must be present.
 * @param options  Auth token plus optional fetch / workerUrl / signal overrides.
 * @returns        The parsed, shape-validated list of objective cards.
 * @throws         When the worker responds with non-200 OR when the
 *                 response payload is missing the `objectives` array.
 */
export async function fetchSuggestedObjectives(
  input: SuggestObjectivesInput,
  options: SuggestObjectivesOptions,
): Promise<SuggestedObjective[]> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.workerUrl ?? resolveWorkerUrl();

  const response = await fetchImpl(`${baseUrl}/objectives/suggest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.accessToken}`,
    },
    body: JSON.stringify(input),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Objective suggestion failed: ${response.status} ${response.statusText}`,
    );
  }

  const payload = (await response.json()) as { objectives?: unknown };
  if (!Array.isArray(payload.objectives)) {
    throw new Error(
      'Objective suggestion response missing `objectives` array.',
    );
  }

  return payload.objectives.filter(isValidSuggestedObjective).map((o) => ({
    title: o.title,
    hint: typeof o.hint === 'string' ? o.hint : '',
    templateKey: o.templateKey,
    ...(typeof o.templateId === 'string' && o.templateId.trim() ? { templateId: o.templateId } : {}),
  }));
}
